import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { 
  Users, 
  BookOpen, 
  Calendar, 
  MessageCircle, 
  Clock,
  Star,
  CreditCard
} from 'lucide-react';

interface DashboardStats {
  totalMatches: number;
  totalSkills: number;
  activeSessions: number;
  unreadMessages: number;
  recentSessions: any[];
  topSkills: any[];
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalMatches: 0,
    totalSkills: 0,
    activeSessions: 0,
    unreadMessages: 0,
    recentSessions: [],
    topSkills: []
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      // Fetch matches
      const matchesResponse = await apiService.getMatches();
      const totalMatches = Object.keys(matchesResponse.data || {}).length;

      // Fetch user skills
      const skillsResponse = await apiService.getUserSkills();
      const totalSkills = skillsResponse.data?.length || 0;

      // Fetch learning sessions
      const sessionsResponse = await apiService.getLearningSessions();
      console.log('Sessions response:', sessionsResponse.data);
      
      const activeSessions = sessionsResponse.data?.filter((session: any) => 
        session.status === 'IN_PROGRESS' || session.status === 'SCHEDULED'
      ).length || 0;

      // Process and validate recent sessions and top skills
      const validSessions = (sessionsResponse.data || []).filter((session: any) => 
        session && 
        session.id && 
        session.skill && 
        session.provider
      );
      
      const recentSessions = validSessions.slice(0, 5);
      const topSkills = (skillsResponse.data || []).slice(0, Math.min(5, skillsResponse.data.length));

      setStats({
        totalMatches,
        totalSkills,
        activeSessions,
        unreadMessages: 0, // Mock data
        recentSessions,
        topSkills
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);



  // Format session date with proper error handling
  const formatSessionDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Date not set';
    
    try {
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting session date:', error);
      return 'Date unavailable';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4 pulse-glow"></div>
          <h2 className="text-xl font-bold gradient-text">Loading Dashboard...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold gradient-text">
              Welcome back, {user?.fullName || user?.username}! 👋
            </h1>
            <p className="text-gray-600 mt-3 text-lg">
              You have <span className="font-semibold text-blue-600">{user?.credits} credits</span> remaining. Ready to learn or teach?
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 group">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-gradient-to-r from-blue-100 to-teal-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Matches</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalMatches}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 group">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-gradient-to-r from-teal-100 to-blue-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <BookOpen className="h-6 w-6 text-teal-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Your Skills</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSkills}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 group">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-gradient-to-r from-blue-100 to-teal-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeSessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 group">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-gradient-to-r from-teal-100 to-blue-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <MessageCircle className="h-6 w-6 text-teal-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Unread Messages</p>
              <p className="text-2xl font-bold text-gray-900">{stats.unreadMessages}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/skills"
            className="flex items-center p-4 border border-blue-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 hover:scale-105 group"
          >
            <div className="h-10 w-10 bg-gradient-to-r from-blue-100 to-teal-100 rounded-lg flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
              <BookOpen className="h-5 w-5 text-blue-600" />
            </div>
            <span className="font-medium text-gray-900">Manage Skills</span>
          </Link>

          <Link
            to="/sessions"
            className="flex items-center p-4 border border-teal-200 rounded-xl hover:bg-teal-50 hover:border-teal-300 transition-all duration-300 hover:scale-105 group"
          >
            <div className="h-10 w-10 bg-gradient-to-r from-teal-100 to-blue-100 rounded-lg flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
              <Calendar className="h-5 w-5 text-teal-600" />
            </div>
            <span className="font-medium text-gray-900">View Sessions</span>
          </Link>

          <Link
            to="/chat"
            className="flex items-center p-4 border border-blue-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 hover:scale-105 group"
          >
            <div className="h-10 w-10 bg-gradient-to-r from-blue-100 to-teal-100 rounded-lg flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
              <MessageCircle className="h-5 w-5 text-blue-600" />
            </div>
            <span className="font-medium text-gray-900">Open Chat</span>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Sessions */}
        <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Recent Sessions</h2>
            <Link to="/sessions" className="text-sm text-blue-600 hover:text-blue-500 font-medium hover:scale-105 transition-transform duration-200">
              View all →
            </Link>
          </div>
          <div className="space-y-3">
            {stats.recentSessions.length > 0 ? (
              stats.recentSessions.map((session: any) => {
                console.log('Rendering session:', session);
                return (
                <div key={session.id} className="flex items-center p-3 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg border border-blue-100 hover:shadow-md transition-all duration-300">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-gradient-to-r from-blue-100 to-teal-100 rounded-lg flex items-center justify-center">
                      <Clock className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {session.skill?.name || 'Unknown Skill'} with {session.provider?.username || 'Unknown User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatSessionDate(session.startTime)}
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                    session.status === 'COMPLETED' ? 'bg-green-100 text-green-800 border border-green-200' :
                    session.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                    session.status === 'SCHEDULED' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                    'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}>
                    {session.status || 'UNKNOWN'}
                  </span>
                </div>
              );
              })
            ) : (
              <div className="text-center py-8">
                <div className="h-16 w-16 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Calendar className="h-8 w-8 text-blue-600" />
                </div>
                <p className="text-gray-500 text-sm">No recent sessions</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Skills */}
        <div className="bg-white rounded-xl shadow-lg border border-teal-100 p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Your Top Skills</h2>
            <Link to="/skills" className="text-sm text-teal-600 hover:text-teal-500 font-medium hover:scale-105 transition-transform duration-200">
              Manage skills →
            </Link>
          </div>
          <div className="space-y-3">
            {stats.topSkills.length > 0 ? (
              stats.topSkills.map((skill: any) => (
                <div key={skill.id} className="flex items-center p-3 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-100 hover:shadow-md transition-all duration-300">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-gradient-to-r from-teal-100 to-blue-100 rounded-lg flex items-center justify-center">
                      <Star className="h-4 w-4 text-teal-600" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-900">{skill.skill?.name}</p>
                    <p className="text-xs text-gray-500">
                      Level {skill.proficiencyLevel} • {skill.relationType}
                    </p>
                  </div>
                  <span className="text-xs text-teal-600 font-medium bg-teal-100 px-2 py-1 rounded-full">
                    {skill.category?.name}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="h-16 w-16 bg-gradient-to-r from-teal-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <BookOpen className="h-8 w-8 text-teal-600" />
                </div>
                <p className="text-gray-500 text-sm">No skills added yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Credits Section */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Credits</h2>
            <p className="text-gray-600 text-lg">You have <span className="font-semibold text-blue-600">{user?.credits}</span> credits remaining</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 bg-gradient-to-r from-blue-100 to-teal-100 rounded-xl flex items-center justify-center">
              <CreditCard className="h-6 w-6 text-blue-600" />
            </div>
            <span className="text-3xl font-bold gradient-text">{user?.credits}</span>
          </div>
        </div>
        <div className="mt-6">
          <Link
            to="/payments"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
          >
            Top Up Credits
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 